import { isWorkflowAgentConversationState } from "@mirage/service-conversation/router_state_types";
import type { Conversation } from "@mirage/service-conversation/types";
import { useMemo } from "react";

export type WorkflowAgentMetadata = {
  agentId: string;
  model: string;
};

export function useWorkflowAgentDetails(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  routerState: any,
  conversation?: Conversation,
): WorkflowAgentMetadata | null {
  const workflowAgentMetadata = useMemo(() => {
    // Attempt to parse Workflow Agent metadata from both the conversation and the router state.
    return (
      parseWorkflowAgentConversation(conversation) ||
      parseWorkflowAgentRouterState(routerState)
    );
  }, [routerState, conversation?.clientContext]);

  // If the conversation is not a Workflow Agent, return null.
  if (!workflowAgentMetadata) {
    return null;
  }

  return workflowAgentMetadata;
}

export function parseWorkflowAgentConversation(
  conversation?: Conversation,
): WorkflowAgentMetadata | null {
  // Attempt to parse the Workflow Agent ID from an existing Conversation.
  // If the conversation does not exist, is missing a clientContext object, or is not
  // a Workflow Agent conversation, returns null.
  if (conversation?.clientContext?.clientScenario === "workflow_agent") {
    const parsedScenarioData = JSON.parse(
      conversation.clientContext.clientScenarioId,
    ) as WorkflowAgentMetadata;
    return {
      agentId: parsedScenarioData.agentId,
      model: parsedScenarioData.model,
    };
  }

  return null;
}

export function parseWorkflowAgentRouterState(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  routerState: any,
): WorkflowAgentMetadata | null {
  // Attempt to parse the Workflow Agent ID from the router state. If the
  // router state does not exist, does not represent a valid Workflow Agent conversation state, or
  // is for a non-Workflow Agent scenario, returns null.
  if (
    routerState &&
    isWorkflowAgentConversationState(routerState) &&
    routerState.scenarioMeta?.type === "workflow_agent"
  ) {
    return {
      agentId: routerState.scenarioMeta.workflowAgentId,
      model: routerState.scenarioMeta.model,
    };
  }

  return null;
}
